/**
 * Return-visit store — отслеживает timestamp последнего захода игрока,
 * persisted в localStorage. Используется для тёплого «вернулся. Роща
 * тебя ждала» welcome'а после > 12h отсутствия.
 *
 * Game store теперь persistится (с 2026-05-25, key `sai-game-state-v1`).
 * Этот store остаётся отдельным потому что (а) timestamp семантика проще
 * чем game state migrations, (б) если потеряем — игрок просто пропустит
 * один greeting, без последствий для прогресса.
 *
 * JB roadmap: hook map (doc 10 §VI) «Дни 1-3 — первый возврат». Doc 26
 * audit §6 (cozy principle #3 «daily rhythm встроенный») — ГЭП.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ReturnVisitStore {
  /** Last time игрок открыл приложение. null = первый заход. */
  readonly lastVisitAt: number | null;
  /** Mark current moment as new visit. Вызывается при mount AppRoot. */
  markVisit(): void;
  /** Test-only reset. */
  _reset(): void;
}

export const useReturnVisitStore = create<ReturnVisitStore>()(
  persist(
    (set) => ({
      lastVisitAt: null,
      markVisit() {
        set({ lastVisitAt: Date.now() });
      },
      _reset() {
        set({ lastVisitAt: null });
      },
    }),
    {
      name: 'sai-return-visit-v1',
    },
  ),
);

/** Сколько часов прошло с последнего захода. null = первый раз. */
export function hoursSinceLastVisit(): number | null {
  const last = useReturnVisitStore.getState().lastVisitAt;
  if (last === null) return null;
  return (Date.now() - last) / (1000 * 60 * 60);
}

/**
 * Бонус осознаний за возвращение. JB doc 26 §7 — таблица:
 *   12-24h → 5 ос.
 *   1-3 дня → 15 ос.
 *   3-7 дней → 30 ос.
 *   7+ дней → 50 ос.
 * Меньше 12h не считается «возвращением» — обычная игровая сессия.
 */
export function returnBonusFor(hours: number): number {
  if (hours < 12) return 0;
  if (hours < 24) return 5;
  if (hours < 72) return 15;
  if (hours < 168) return 30;
  return 50;
}

/** Тёплое сообщение по длине отсутствия. Все в языке присутствия. */
export function returnGreetingFor(hours: number): string {
  if (hours < 24) return 'вернулся. Роща тебя помнит.';
  if (hours < 72) return 'ты вернулся. Искра ждала.';
  if (hours < 168) return 'дни прошли — но твой ритм остался.';
  return 'долго ты был. Роща терпелива. ты дома.';
}
